import React, { useCallback, useMemo } from 'react';

import { type EntryPoint, type EntryPointComponent } from 'react-relay';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import { ModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';
import { ModalTrigger, type Props } from '@atlassian/entry-point-modal-trigger';

import { useEntrypointErrorAnalytics } from '../../../analytics/use-entrypoint-error-analytics';
import { useOnCloseDialog } from '../../../analytics/use-on-close-dialog';
import { EditionAwarenessButton } from '../../edition-awareness-button';

import { DialogConfig } from './types';

type AnyEntryPoint = EntryPoint<EntryPointComponent<any, any, any, any>, any>;

type ModalEntryPointInnerProps<T> = Omit<Props<T>, 'children'>;
type ModalEntryPointProps<T> = ModalEntryPointInnerProps<T> & {
	dialogConfig: DialogConfig;
};

export function ModalEntryPoint<TEntryPoint extends AnyEntryPoint>(
	props: ModalEntryPointProps<TEntryPoint>,
) {
	const { dialogConfig, ...rest } = props;

	return (
		<AnalyticsContext data={dialogConfig}>
			<ModalEntryPointInner<TEntryPoint> {...rest} />
		</AnalyticsContext>
	);
}

function ModalEntryPointInner<TEntryPoint extends AnyEntryPoint>(
	props: ModalEntryPointInnerProps<TEntryPoint>,
) {
	const { entryPointProps, ...rest } = props;

	const onError = useEntrypointErrorAnalytics();
	const onDismiss = useOnCloseDialog();

	const onClose = useCallback(() => {
		onDismiss();
		entryPointProps?.onClose?.();
	}, [entryPointProps, onDismiss]);

	const finalEntryPointProps = useMemo(
		() => ({ ...entryPointProps, onClose }) as typeof entryPointProps,
		[entryPointProps, onClose],
	);

	return (
		<ModalContextProvider>
			<ModalTrigger<TEntryPoint> {...rest} entryPointProps={finalEntryPointProps} onError={onError}>
				{({ ref }) => {
					return <EditionAwarenessButton ref={ref as unknown as React.Ref<HTMLButtonElement>} />;
				}}
			</ModalTrigger>
		</ModalContextProvider>
	);
}
